"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type AnimalFlowDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A sunlit terracotta studio: golden-hour light pours through an arched
 * window on the left, a monstera throws leaf shadows on the right, and a mover
 * holds "beast" — the signature Animal Flow position, hands and feet down,
 * knees hovering — on a mat in the middle (the focal point). Paw prints wander
 * in along the floor. The floor under the bottom-centre participant pill stays
 * open.
 *
 * Motion: the window light breathes — one CSS opacity loop, off under reduced
 * motion.
 */
const ANIMAL_FLOW_KEYFRAMES = `
@keyframes animal-flow-light { 0%, 100% { opacity: 0.75 } 50% { opacity: 1 } }
.animal-flow-light { animation: animal-flow-light 6s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .animal-flow-light { animation: none }
}
`;

/** Floor line, in px from the top of the 220px scene. */
const HORIZON = 138;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `animal-flow-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("wall")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#d98a58" />
          <stop offset="0.45" stopColor="#b9683f" />
          <stop offset="1" stopColor="#7d3f22" />
        </linearGradient>
        <linearGradient id={id("floor")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#9a5d33" />
          <stop offset="1" stopColor="#4a2814" />
        </linearGradient>
        <linearGradient id={id("beam")} x1="0" x2="1" y1="0" y2="0.6">
          <stop offset="0" stopColor="#ffe2a8" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffe2a8" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffd8a0" />
          <stop offset="0.6" stopColor="#ffb878" />
          <stop offset="1" stopColor="#f28d5c" />
        </linearGradient>
        <linearGradient id={id("pot")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#e9d5bd" />
          <stop offset="1" stopColor="#a68b6e" />
        </linearGradient>
        <linearGradient id={id("mat")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#7d8a46" />
          <stop offset="1" stopColor="#56622c" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Wall, floorboards, the window's beam and floor patch — stretches. */
function Studio({ id }: { id: IdFn }) {
  const h = HORIZON;
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="300" height={h} fill={`url(#${id("wall")})`} />
      {/* Plaster mottling */}
      <rect x="0" y="0" width="300" height={h} fill="#000" opacity="0.06" />
      {/* Skirting */}
      <rect x="0" y={h - 6} width="300" height="6" fill="#5e2f18" />
      <rect
        x="0"
        y={h - 6}
        width="300"
        height="1"
        fill="#ffcf9a"
        opacity="0.35"
      />
      {/* Floor + boards converging on the window side */}
      <rect
        x="0"
        y={h}
        width="300"
        height={220 - h}
        fill={`url(#${id("floor")})`}
      />
      {[0, 40, 80, 120, 160, 200, 240, 280].map((x) => (
        <line
          key={x}
          x1={x + 10}
          y1={h}
          x2={(x + 10 - 150) * 2.2 + 150}
          y2="220"
          stroke="#2a1508"
          strokeOpacity="0.4"
          strokeWidth="0.8"
        />
      ))}
      {/* Sun patch from the window */}
      <g className="animal-flow-light">
        <polygon
          points={`20,40 74,40 230,${h + 40} 120,${h + 40}`}
          fill={`url(#${id("beam")})`}
        />
        <polygon
          points={`96,${h + 6} 200,${h + 6} 250,${h + 46} 120,${h + 46}`}
          fill="#ffd38c"
          opacity="0.22"
        />
      </g>
    </svg>
  );
}

function ArchedWindow({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-[30px] left-[5%]"
      width="64"
      height="104"
      viewBox="0 0 64 104"
      aria-hidden="true"
      focusable="false"
    >
      {/* Deep reveal */}
      <path d="M0 104 V32 A32 32 0 0 1 64 32 V104 Z" fill="#6b341a" />
      {/* Glass: dusk sky + distant hills */}
      <path
        d="M5 104 V33 A27 27 0 0 1 59 33 V104 Z"
        fill={`url(#${id("sky")})`}
      />
      <circle cx="40" cy="58" r="9" fill="#fff3d0" opacity="0.9" />
      <path
        d="M5 80 Q20 70 34 76 Q48 68 59 74 V104 H5 Z"
        fill="#c9774a"
        opacity="0.7"
      />
      <path
        d="M5 90 Q24 82 40 88 Q52 84 59 88 V104 H5 Z"
        fill="#9e5530"
        opacity="0.8"
      />
      {/* Mullions */}
      <rect x="31" y="6" width="2" height="98" fill="#4a2410" />
      <rect x="5" y="56" width="54" height="2" fill="#4a2410" />
      {/* Sill */}
      <rect x="-2" y="100" width="68" height="4" fill="#e4b98a" />
    </svg>
  );
}

function Monstera({ id }: { id: IdFn }) {
  const leaf =
    "M0 0 C 10 -14, 30 -14, 36 0 C 30 12, 10 14, 0 0 Z M8 -4 L14 -9 M14 1 L22 -7 M20 4 L28 -4";
  return (
    <svg
      className="absolute right-[2%]"
      style={{ bottom: 220 - HORIZON - 8 }}
      width="86"
      height="124"
      viewBox="0 0 86 124"
      aria-hidden="true"
      focusable="false"
    >
      {/* Leaf shadows thrown on the wall behind */}
      <g opacity="0.16" fill="#3a1a0a">
        <path d="M8 22 C 18 8, 36 8, 42 22 C 36 34, 18 36, 8 22 Z" />
        <path d="M2 54 C 10 42, 26 42, 30 54 C 26 64, 10 66, 2 54 Z" />
      </g>
      {/* Stems */}
      <path
        d="M54 98 Q50 70 40 44 M56 98 Q60 66 72 38 M54 98 Q46 82 26 74 M58 98 Q70 84 80 72"
        stroke="#2e5a2a"
        strokeWidth="2"
        fill="none"
      />
      {/* Leaves */}
      {[
        { t: "translate(24 44) rotate(-40)", f: "#2f7a3b" },
        { t: "translate(60 38) rotate(-75)", f: "#3a8c45" },
        { t: "translate(10 74) rotate(-12)", f: "#24622f" },
        { t: "translate(66 72) rotate(-20) scale(0.8)", f: "#2f7a3b" },
      ].map((l) => (
        <g key={l.t} transform={l.t}>
          <path d={leaf} fill={l.f} />
          <path
            d="M0 0 L36 0 M10 -6 L12 -11 M18 4 L22 10"
            stroke="#1a3f1c"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M0 0 C 10 -14, 30 -14, 36 0"
            stroke="#bfe39a"
            strokeOpacity="0.5"
            strokeWidth="0.9"
            fill="none"
          />
        </g>
      ))}
      {/* Pot */}
      <path d="M38 96 H74 L70 124 H42 Z" fill={`url(#${id("pot")})`} />
      <rect x="36" y="94" width="40" height="5" rx="1.5" fill="#f3e4cf" />
    </svg>
  );
}

/** The mover, holding beast: hands and feet down, knees hovering. */
function BeastFigure() {
  const skin = "#7a4628";
  const farSkin = "#55301a";
  const top = "#f1e2c6";
  const tights = "#2b2238";
  const farTights = "#1b1524";
  const rim = "#ffd59a";
  return (
    <svg
      className="absolute left-[calc(50%-50px)]"
      style={{ top: HORIZON - 34 }}
      width="100"
      height="60"
      viewBox="0 0 100 60"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="50" cy="57" rx="44" ry="2.4" fill="#000" opacity="0.35" />
      {/* Far limbs */}
      <path
        d="M27 28 L25 55"
        stroke={farSkin}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M66 27 L66 48 L86 54"
        stroke={farTights}
        strokeWidth="6.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Flat table-top back: cropped top, then tights */}
      <path
        d="M44 26 L66 25"
        stroke={tights}
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M22 27 L46 26"
        stroke={top}
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
      />
      {/* Near limbs */}
      <path
        d="M20 29 L16 55"
        stroke={skin}
        strokeWidth="5.5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M64 28 L58 47 L80 55"
        stroke={tights}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Feet + hands */}
      <path
        d="M78 55 L84 55"
        stroke={skin}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M13 55.5 L19 55.5"
        stroke={skin}
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* Head + bun, gaze down at the hands */}
      <circle cx="11" cy="25" r="6.2" fill={skin} />
      <path d="M5 23 A6.2 6.2 0 0 1 16.5 21.6 L12 22 Z" fill="#24140b" />
      <circle cx="8.5" cy="18.4" r="3" fill="#24140b" />
      {/* Window-side rim light */}
      <path
        d="M17 21.4 L66 19.4 M5.2 23 A6.2 6.2 0 0 1 9 18.9 M14.2 29 L10.8 55"
        stroke={rim}
        strokeOpacity="0.85"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function Mat({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute left-[calc(50%-78px)]"
      style={{ top: HORIZON + 14 }}
      width="156"
      height="16"
      viewBox="0 0 156 16"
      aria-hidden="true"
      focusable="false"
    >
      <polygon points="14,0 142,0 156,14 0,14" fill={`url(#${id("mat")})`} />
      <polygon points="0,14 156,14 156,16 0,16" fill="#3a4219" />
      <line
        x1="14"
        y1="0.5"
        x2="142"
        y2="0.5"
        stroke="#d6e29a"
        strokeOpacity="0.5"
      />
    </svg>
  );
}

function PawPrints() {
  const paw = (
    <>
      <ellipse cx="5" cy="7" rx="3.4" ry="2.8" />
      <circle cx="1.4" cy="2.6" r="1.3" />
      <circle cx="4" cy="0.9" r="1.3" />
      <circle cx="7" cy="1.1" r="1.3" />
      <circle cx="9.2" cy="3.2" r="1.3" />
    </>
  );
  return (
    <svg
      className="absolute bottom-[10px] left-[3%]"
      width="72"
      height="44"
      viewBox="0 0 72 44"
      aria-hidden="true"
      focusable="false"
      fill="#ffe2bd"
      opacity="0.32"
    >
      <g transform="translate(2 30) rotate(-30 5 5)">{paw}</g>
      <g transform="translate(20 34) rotate(-20 5 5)">{paw}</g>
      <g transform="translate(30 16) rotate(-35 5 5)">{paw}</g>
      <g transform="translate(48 20) rotate(-20 5 5)">{paw}</g>
      <g transform="translate(58 2) rotate(-30 5 5) scale(0.85)">{paw}</g>
    </svg>
  );
}

export function AnimalFlowDisplayPreview({
  participants,
}: AnimalFlowDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Animal Flow"
      backgroundClassName="bg-[#8c4a2b]"
      participants={participants}
      scene={
        <>
          <style href="animal-flow-scene-keyframes" precedence="default">
            {ANIMAL_FLOW_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          <Studio id={id} />
          <ArchedWindow id={id} />
          <Monstera id={id} />
          <Mat id={id} />
          <BeastFigure />
          <PawPrints />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_35%_35%,rgba(255,214,150,0.12)_0%,transparent_45%,rgba(40,14,4,0.55)_100%)]" />
      }
    />
  );
}
